import type { Theme } from '@emotion/react'
import {
  EyeClosedIcon,
  EyeIcon,
  FilterIcon,
  SearchIcon,
} from '@moia-dev/pace-icons'
import { Icon, Input, Text, useDialog } from '@moia-dev/pace-web'
import type * as maplibregl from 'maplibre-gl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ODD_TOGGLE_GROUPS, ODD_TOGGLE_TYPES } from '../layer-config'
import { oddLayerId } from '../map-layers'
import { getOddIconSrc, getOddLegendSvg, ODD_PNG_MAP } from '../odd-icons'
import { useMapOverlays } from '../useMapOverlays'
import {
  useOperatingAreas,
  useOperatingAreasLayers,
} from '../useOperatingAreasOverlay'
import { useRoadBlockagesOverlay } from '../useRoadBlockagesOverlay'
import {
  EMPTY_HEATMAP_FILTERS,
  HeatmapFilterModal,
  type HeatmapFilters,
  hasActiveFilters,
} from './HeatmapFilterModal'
import {
  categoryColor,
  type HeatmapFilterOptions,
  HeatmapLayer,
  type HeatmapLayerType,
} from './HeatmapLayer'
import {
  DEFAULT_FILTER_CONFIG,
  type FilteredElements,
  type OddFilterConfig,
  OddFilterModal,
} from './OddFilterModal'
import { OverlayPanel } from './OverlayPanel'
import { OverlaySection } from './OverlaySection'
import { OverlayToggle } from './OverlayToggle'

export type ODDMapElement = {
  id: string
  type: string
  location: { latitude: number; longitude: number }
  name: string | null
}

const unavailableTextStyle = (theme: Theme) => ({
  color: theme.semantic.ColorContentReduced,
  fontStyle: 'italic',
})

type MapOverlayOptionsProps = {
  map: maplibregl.Map | null
  metropolisId: number
  metropolisName?: string
  onOddElementClick?: (lat: number, lng: number, type: string) => void
  routeOddElements?: ODDMapElement[]
  vtrRequiredTypes?: string[]
  autoEnableLanes?: boolean
  autoEnableGeonets?: boolean
  showGeonets?: boolean
  showLanes?: boolean
  /** Layer ID to insert ODD/geonet layers before (e.g. the route line) */
  insertBeforeLayer?: string
  /** When false, non-route ODDs are not dimmed even when routeOddElements is set. Default true. */
  dimNonRouteOdds?: boolean
  /**
   * When true, the initial auto-enable of route ODD types uses the preloaded
   * `routeOddElements` (route-only, rendered at full opacity) instead of
   * fetching the full metro set. Manual toggles still fetch the full metro set
   * and dim the non-route ODDs. Used by trip-history to keep the initial view
   * route-only while still allowing on-demand exploration of surrounding ODDs.
   * Default false.
   */
  autoEnableRouteOddsOnly?: boolean
  /** When false, the Heatmaps section is hidden. Default true. */
  showHeatmaps?: boolean
  /** When true, the Road Blockages section is shown. Default false. */
  showRoadBlockages?: boolean
  /** When false, the operating area is not drawn. Default true. */
  showOperatingAreas?: boolean
  /** Fires whenever the active-ODD state flips, so a layers button can badge it. */
  onActiveLayersChange?: (hasActiveLayers: boolean) => void
  /**
   * Fires with the set of currently-visible ODD types whenever it changes. Lets a
   * consumer that renders its own route-ODD markers (route-builder's RouteMap)
   * keep them in sync with the sidebar toggles, so turning a type off — or the
   * route dropping it — hides those markers too.
   */
  onActiveOddTypesChange?: (activeTypes: string[]) => void
}

export const MapOverlayOptions = ({
  map,
  metropolisId,
  metropolisName,
  onOddElementClick,
  routeOddElements = [],
  vtrRequiredTypes = [],
  autoEnableLanes = false,
  autoEnableGeonets = false,
  showGeonets = true,
  showLanes = true,
  insertBeforeLayer,
  dimNonRouteOdds = true,
  autoEnableRouteOddsOnly = false,
  showHeatmaps = true,
  showRoadBlockages = false,
  showOperatingAreas = true,
  onActiveLayersChange,
  onActiveOddTypesChange,
}: MapOverlayOptionsProps) => {
  const onOddElementClickRef = useRef(onOddElementClick)
  onOddElementClickRef.current = onOddElementClick
  const onActiveLayersChangeRef = useRef(onActiveLayersChange)
  onActiveLayersChangeRef.current = onActiveLayersChange
  const onActiveOddTypesChangeRef = useRef(onActiveOddTypesChange)
  onActiveOddTypesChangeRef.current = onActiveOddTypesChange

  const [oddSearch, setOddSearch] = useState('')
  const [activeFilterType, setActiveFilterType] = useState<string | null>(null)
  const [oddFilterConfigs, setOddFilterConfigs] = useState<
    Record<string, OddFilterConfig>
  >({})
  const [roadBlockagesExpanded, setRoadBlockagesExpanded] = useState(false)
  const operatingAreas = useOperatingAreas({
    metropolisId,
    metropolisName,
    enabled: showOperatingAreas,
  })
  const roadBlockages = useRoadBlockagesOverlay(
    map,
    metropolisName,
    insertBeforeLayer,
  )

  // Heatmap state
  const [heatmapExpanded, setHeatmapExpanded] = useState(false)
  const [activeHeatmap, setActiveHeatmap] = useState<HeatmapLayerType>(null)
  const [heatmapLoading, setHeatmapLoading] = useState(false)
  const [heatmapFilters, setHeatmapFilters] = useState<HeatmapFilters>(
    EMPTY_HEATMAP_FILTERS,
  )
  const [heatmapFilterOptions, setHeatmapFilterOptions] =
    useState<HeatmapFilterOptions | null>(null)
  const [heatmapCategories, setHeatmapCategories] = useState<string[]>([])
  const [hiddenHeatmapCategories, setHiddenHeatmapCategories] = useState<
    Set<string>
  >(new Set())
  // True only after the first data load completes — used to gate the filter icon
  // so it never blinks before data is on the map.
  const [heatmapDataLoaded, setHeatmapDataLoaded] = useState(false)
  const heatmapFilterDialog = useDialog()

  const toggleHeatmapCategory = useCallback((cat: string) => {
    setHiddenHeatmapCategories(prev => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }, [])

  // Reset active heatmap when metro changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: metropolisId is the trigger; state setters are stable
  useEffect(() => {
    setActiveHeatmap(null)
    setHeatmapFilters(EMPTY_HEATMAP_FILTERS)
    setHeatmapFilterOptions(null)
    setHeatmapCategories([])
    setHiddenHeatmapCategories(new Set())
    setHeatmapDataLoaded(false)
    heatmapFilterDialog.closeDialog()
  }, [metropolisId])

  const hasRoute = routeOddElements.length > 0
  const routeOpacity = dimNonRouteOdds && hasRoute ? 0.15 : 1

  // routeCountsByType: per-type count of elements along the route.
  // Only relevant in route-builder mode (dimNonRouteOdds=true) where the total
  // count is the full metro set and route elements are a subset.
  const routeCountsByType = useMemo(() => {
    if (!dimNonRouteOdds || !hasRoute) return null
    const counts: Record<string, number> = {}
    for (const el of routeOddElements) {
      counts[el.type] = (counts[el.type] ?? 0) + 1
    }
    return counts
  }, [dimNonRouteOdds, hasRoute, routeOddElements])

  const overlays = useMapOverlays({
    map,
    metropolisId,
    metropolisName,
    oddIconOpacity: routeOpacity,
    // The hardcoded geonets start off when the live operating area is shown.
    // Waiting until that is known keeps them from flashing on and off.
    autoEnableGeonets:
      autoEnableGeonets && operatingAreas.settled && !operatingAreas.isLive,
    autoEnableLanes,
    geonetOutlineOnly: operatingAreas.isLive,
    laneClipArea: operatingAreas.area,
    routeOddElements,
    insertBeforeLayer,
  })
  // Stable key of the hardcoded geonets on the map with their data loaded, so
  // the mask is only rebuilt when that set changes.
  const shownGeonetIdsKey = overlays.geonetConfigs
    .filter(
      config =>
        overlays.visible[config.id] && !overlays.layerLoading[config.id],
    )
    .map(config => config.id)
    .join(',')
  const { getLoadedLayerData } = overlays
  const shownGeonetOutlines = useMemo(
    () =>
      shownGeonetIdsKey
        .split(',')
        .map(id => (id ? getLoadedLayerData(id) : undefined))
        .filter(outline => outline !== undefined),
    [shownGeonetIdsKey, getLoadedLayerData],
  )
  useOperatingAreasLayers({
    map,
    area: operatingAreas.area,
    shownGeonetOutlines,
    insertBeforeLayer,
  })
  const {
    setOddOpacity,
    expandOdd,
    enableOddType,
    disableOddType,
    oddGroupExpanded,
    toggleOddGroup,
    resetAllOdd,
    syncRouteOddTypes,
  } = overlays

  useEffect(() => {
    if (!map) return

    const handleMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer'
    }
    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = ''
    }

    const handlers: Array<{
      layerId: string
      click: (
        e: maplibregl.MapMouseEvent & {
          features?: maplibregl.MapGeoJSONFeature[]
        },
      ) => void
    }> = []

    for (const { type } of ODD_TOGGLE_TYPES) {
      const layerId = oddLayerId(type)
      const capturedType = type
      const handleClick = (
        e: maplibregl.MapMouseEvent & {
          features?: maplibregl.MapGeoJSONFeature[]
        },
      ) => {
        const feature = e.features?.[0]
        if (feature?.geometry.type !== 'Point') return
        const [lng, lat] = feature.geometry.coordinates
        onOddElementClickRef.current?.(lat, lng, capturedType)
      }
      handlers.push({ layerId, click: handleClick })
      map.on('click', layerId, handleClick)
      map.on('mouseenter', layerId, handleMouseEnter)
      map.on('mouseleave', layerId, handleMouseLeave)
    }

    return () => {
      for (const { layerId, click } of handlers) {
        map.off('click', layerId, click)
        map.off('mouseenter', layerId, handleMouseEnter)
        map.off('mouseleave', layerId, handleMouseLeave)
      }
    }
  }, [map])

  useEffect(() => {
    setOddOpacity(routeOpacity)
  }, [routeOpacity, setOddOpacity])

  // Reconcile route-driven ODDs against the current route on every change. Unlike
  // a reset-only-on-empty approach, this diffs the types so ODDs turn off as soon
  // as they drop off the route (route shrinks, trip switches) or the route clears
  // entirely — mirroring the dim-opacity reset. Manual off-route selections are
  // tracked separately in the hook and survive.
  useEffect(() => {
    const typesOnRoute = [...new Set(routeOddElements.map(el => el.type))]
    syncRouteOddTypes(
      typesOnRoute,
      // Trip-history (autoEnableRouteOddsOnly): use the preloaded route elements
      // and skip the GraphQL fetch, so the initial view shows only route ODDs.
      // Route-builder: fetch all metro ODDs and dim the non-route ones.
      autoEnableRouteOddsOnly ? routeOddElements : undefined,
    )
  }, [routeOddElements, autoEnableRouteOddsOnly, syncRouteOddTypes])

  // Auto-enable the ODD types required by the selected VTR requirement, and undo
  // that auto-enable when the requirement changes or is cleared (e.g. navigating
  // back). Only types this effect turned on are disabled again — types the user
  // toggled on manually are tracked separately and left untouched.
  const vtrAutoEnabledRef = useRef(new Set<string>())
  const prevVtrTypesRef = useRef('')
  useEffect(() => {
    const key = [...vtrRequiredTypes].sort().join(',')
    if (key === prevVtrTypesRef.current) return
    prevVtrTypesRef.current = key

    const required = new Set(vtrRequiredTypes)

    // Disable types that were auto-enabled for the previous requirement but are
    // no longer required.
    for (const type of vtrAutoEnabledRef.current) {
      if (!required.has(type)) {
        disableOddType(type)
        vtrAutoEnabledRef.current.delete(type)
      }
    }

    if (vtrRequiredTypes.length === 0) return

    expandOdd()
    for (const type of vtrRequiredTypes) {
      enableOddType(type)
      vtrAutoEnabledRef.current.add(type)
    }
  }, [vtrRequiredTypes, expandOdd, enableOddType, disableOddType])

  const search = oddSearch.trim().toLowerCase()

  const activeOddTypes = ODD_TOGGLE_TYPES.filter(
    ({ type }) => overlays.visible[`odd-${type}`],
  ).map(({ type }) => type)
  const hasActiveOdd = activeOddTypes.length > 0
  // Stable key so the report effect only fires when the set actually changes,
  // not on every render (activeOddTypes is a fresh array each time).
  const activeOddTypesKey = activeOddTypes.join(',')

  useEffect(() => {
    onActiveLayersChangeRef.current?.(hasActiveOdd)
  }, [hasActiveOdd])

  useEffect(() => {
    onActiveOddTypesChangeRef.current?.(
      activeOddTypesKey ? activeOddTypesKey.split(',') : [],
    )
  }, [activeOddTypesKey])

  const handleFilterSave = (
    type: string,
    config: OddFilterConfig,
    elements: FilteredElements | null,
  ) => {
    setOddFilterConfigs(prev => ({ ...prev, [type]: config }))
    setActiveFilterType(null)
    if (overlays.visible[`odd-${type}`]) {
      overlays.toggleOdd(type, false)
      setTimeout(() => {
        overlays.toggleOdd(type, true, elements ?? undefined)
      }, 0)
    }
  }

  const activeFilterItem = activeFilterType
    ? ODD_TOGGLE_GROUPS.flatMap(g => g.items).find(
        i => i.type === activeFilterType,
      )
    : null

  return (
    <>
      <OverlayPanel>
        {showGeonets && (
          <OverlaySection
            title="Geonets"
            expanded={overlays.expanded.geonets}
            onToggle={() => overlays.toggleSection('geonets')}
          >
            {overlays.geonetConfigs.length === 0 ? (
              <Text size="sm" css={unavailableTextStyle}>
                No geonets available for this metro
              </Text>
            ) : (
              overlays.geonetConfigs.map(config => (
                <OverlayToggle
                  key={config.id}
                  label={config.label}
                  color={config.color}
                  checked={overlays.visible[config.id] ?? false}
                  onChange={checked => overlays.toggleLayer(config, checked)}
                  loading={overlays.layerLoading[config.id] ?? false}
                />
              ))
            )}
          </OverlaySection>
        )}

        {showLanes && (
          <OverlaySection
            title="Lanes"
            expanded={overlays.expanded.lanes}
            onToggle={() => overlays.toggleSection('lanes')}
          >
            {overlays.laneConfigs.length === 0 ? (
              <Text size="sm" css={unavailableTextStyle}>
                No lanes available for this metro
              </Text>
            ) : (
              overlays.laneConfigs.map(config => (
                <OverlayToggle
                  key={config.id}
                  label={config.label}
                  color={config.color}
                  checked={overlays.visible[config.id] ?? false}
                  onChange={checked => overlays.toggleLayer(config, checked)}
                  loading={overlays.layerLoading[config.id] ?? false}
                />
              ))
            )}
          </OverlaySection>
        )}

        {showRoadBlockages && (
          <OverlaySection
            title="Road Blockages"
            expanded={roadBlockagesExpanded}
            onToggle={() => setRoadBlockagesExpanded(prev => !prev)}
          >
            <OverlayToggle
              label="Active & Scheduled"
              color="#D32F2F"
              checked={roadBlockages.visible}
              onChange={checked => roadBlockages.toggle(checked)}
              loading={roadBlockages.loading}
            />
          </OverlaySection>
        )}

        {showHeatmaps && (
          <OverlaySection
            title="Heatmaps"
            expanded={heatmapExpanded}
            onToggle={() => setHeatmapExpanded(e => !e)}
          >
            {(
              [
                { id: 'pce', label: 'Potential Critical Events' },
                { id: 'disengagements', label: 'Disengagements' },
                { id: 'geonetCoverage', label: 'Geonet Coverage' },
              ] as const
            ).map(({ id, label }) => (
              <div
                key={id}
                css={{
                  '&:hover .heatmap-filter-btn': {
                    opacity: 1,
                    visibility: 'visible',
                  },
                }}
              >
                {/* Toggle row wrapper — the filter button is positioned relative
                    to this row only, so it stays aligned with the title and does
                    not drift down when the sub-item list below expands. */}
                <div css={{ position: 'relative' }}>
                  <OverlayToggle
                    label={label}
                    hideIcon
                    checked={activeHeatmap === id}
                    loading={activeHeatmap === id && heatmapLoading}
                    onChange={checked => {
                      setActiveHeatmap(checked ? id : null)
                      if (!checked) {
                        setHeatmapFilters(EMPTY_HEATMAP_FILTERS)
                        setHeatmapFilterOptions(null)
                        setHeatmapCategories([])
                        setHiddenHeatmapCategories(new Set())
                        setHeatmapDataLoaded(false)
                      }
                    }}
                  />
                  {activeHeatmap === id && heatmapDataLoaded && (
                    <button
                      type="button"
                      className="heatmap-filter-btn"
                      title="Filter heatmap data"
                      onClick={e => {
                        e.stopPropagation()
                        heatmapFilterDialog.openDialog()
                      }}
                      css={{
                        position: 'absolute',
                        right: 44,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        padding: 2,
                        cursor: 'pointer',
                        color: hasActiveFilters(heatmapFilters)
                          ? '#0532ff'
                          : '#555',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 4,
                        '&:hover': { background: 'rgba(0,0,0,0.06)' },
                      }}
                    >
                      <Icon size={16}>
                        <FilterIcon />
                      </Icon>
                    </button>
                  )}
                </div>

                {/* Sub-items: per-category visibility rows (PCE & Disengagements) */}
                {id !== 'geonetCoverage' && (
                  <div
                    css={{
                      overflow: 'hidden',
                      maxHeight:
                        activeHeatmap === id &&
                        heatmapDataLoaded &&
                        heatmapCategories.length > 0
                          ? heatmapCategories.length * 28 + 4
                          : 0,
                      opacity:
                        activeHeatmap === id &&
                        heatmapDataLoaded &&
                        heatmapCategories.length > 0
                          ? 1
                          : 0,
                      transition:
                        'max-height 230ms ease-in-out, opacity 200ms ease',
                    }}
                  >
                    {heatmapCategories.map((cat, idx) => {
                      const dotColor = categoryColor(idx)
                      const isHidden = hiddenHeatmapCategories.has(cat)
                      return (
                        <div
                          key={cat}
                          css={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: 8,
                            height: 28,
                            gap: 8,
                          }}
                        >
                          <div
                            css={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: dotColor,
                              flexShrink: 0,
                            }}
                          />
                          <Text
                            size="sm"
                            css={{
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {cat}
                          </Text>
                          <button
                            type="button"
                            title={isHidden ? 'Show on map' : 'Hide on map'}
                            onClick={e => {
                              e.stopPropagation()
                              toggleHeatmapCategory(cat)
                            }}
                            css={{
                              background: 'none',
                              border: 'none',
                              padding: 2,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              color: isHidden ? '#aaa' : '#555',
                              borderRadius: 4,
                              '&:hover': { background: 'rgba(0,0,0,0.06)' },
                            }}
                          >
                            <Icon size={16}>
                              {isHidden ? <EyeClosedIcon /> : <EyeIcon />}
                            </Icon>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}
          </OverlaySection>
        )}

        <OverlaySection
          title="ODD Elements"
          expanded={overlays.expanded.odd}
          onToggle={() => overlays.toggleSection('odd')}
          scrollable
          footer={
            hasActiveOdd && overlays.zoomLevel < 14 ? (
              <div
                css={theme => ({
                  margin: `${theme.space.Space1} -${theme.space.Space2} -${theme.space.Space1}`,
                  padding: `${theme.space.Space2} ${theme.space.Space4}`,
                  background: theme.semantic.ColorBackgroundFixedDefault,
                  color: theme.semantic.ColorContentOnBackgroundFixedDefault,
                  fontWeight: 500,
                  textAlign: 'center',
                  borderRadius: '0 0 4px 4px',
                  flexShrink: 0,
                })}
              >
                Zoom in to see ODD Elements
              </div>
            ) : undefined
          }
        >
          <Input
            size="compact"
            placeholder="Search"
            value={oddSearch}
            onChange={e => setOddSearch(e.target.value)}
            leading={<SearchIcon />}
            css={theme => ({ marginBottom: theme.space.Space1 })}
          />

          <button
            type="button"
            onClick={resetAllOdd}
            css={theme => ({
              background: 'none',
              border: 'none',
              padding: `2px 0 ${theme.space.Space2} 0`,
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: 11,
              fontWeight: 600,
              color: hasActiveOdd
                ? theme.semantic.ColorAccentDefault
                : theme.semantic.ColorContentReduced,
              '&:hover': { textDecoration: 'underline' },
            })}
          >
            Reset all ODD Elements
          </button>

          {ODD_TOGGLE_GROUPS.map(group => {
            const withIcon = group.items.filter(
              ({ type }) => type in ODD_PNG_MAP,
            )
            const filtered = withIcon.filter(
              ({ label }) =>
                !search || label.toLowerCase().includes(search.toLowerCase()),
            )
            const activeCount = group.items.filter(
              ({ type }) => overlays.visible[`odd-${type}`],
            ).length
            const isExpanded = oddGroupExpanded[group.section] ?? true

            return (
              <div
                key={group.section}
                css={theme => ({
                  '& + &': {
                    borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
                    marginTop: theme.space.Space1,
                    paddingTop: theme.space.Space1,
                  },
                })}
              >
                <button
                  type="button"
                  onClick={() => toggleOddGroup(group.section)}
                  css={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    padding: '2px 0',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7 },
                  }}
                >
                  <Text weight="bold" size="sm">
                    {group.section}
                  </Text>
                  {activeCount > 0 && (
                    <div
                      css={theme => ({
                        position: 'absolute',
                        right: 20,
                        backgroundColor: theme.semantic.ColorAccentDefault,
                        color: theme.semantic.ColorContentOnAccentDefault,
                        fontSize: 10,
                        fontWeight: 700,
                        minWidth: 18,
                        height: 18,
                        borderRadius: 9,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: `0 ${theme.space.Space1}`,
                      })}
                    >
                      {activeCount}
                    </div>
                  )}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    css={{
                      flexShrink: 0,
                      transition: 'transform 150ms ease',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  >
                    <path
                      d="M2 4l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                <div
                  css={{
                    display: 'grid',
                    gridTemplateRows: isExpanded ? '1fr' : '0fr',
                    transition: 'grid-template-rows 200ms ease',
                  }}
                >
                  <div css={{ overflow: 'hidden' }}>
                    {filtered.length === 0 ? (
                      <Text
                        size="sm"
                        css={theme => ({
                          color: theme.semantic.ColorContentReduced,
                          fontStyle: 'italic',
                        })}
                      >
                        {withIcon.length === 0 || !search
                          ? 'No elements available'
                          : 'No matching elements'}
                      </Text>
                    ) : (
                      filtered.map(({ type, label }) => {
                        const isFilterActive =
                          !!oddFilterConfigs[type] &&
                          (oddFilterConfigs[type].containsEnabled ||
                            oddFilterConfigs[type].excludesEnabled)
                        return (
                          <div
                            key={type}
                            css={{
                              position: 'relative',
                              '&:hover .odd-filter-btn': {
                                opacity: 1,
                                visibility: 'visible',
                              },
                            }}
                          >
                            <OverlayToggle
                              label={label}
                              checked={overlays.visible[`odd-${type}`] ?? false}
                              count={overlays.oddCounts[type]}
                              routeCount={routeCountsByType?.[type]}
                              onChange={checked =>
                                overlays.toggleOddManual(type, checked)
                              }
                              iconSrc={getOddIconSrc(type, 24)}
                              iconSvg={getOddLegendSvg(type)}
                              useCheckbox
                              loading={overlays.oddLoading[type] ?? false}
                            />
                            <button
                              type="button"
                              className="odd-filter-btn"
                              title="Filter attributes"
                              onClick={e => {
                                e.stopPropagation()
                                setActiveFilterType(type)
                              }}
                              css={theme => ({
                                position: 'absolute',
                                right: 26,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                padding: 2,
                                cursor: 'pointer',
                                opacity: isFilterActive ? 1 : 0,
                                visibility: isFilterActive
                                  ? 'visible'
                                  : 'hidden',
                                color: isFilterActive
                                  ? theme.semantic.ColorAccentDefault
                                  : theme.semantic.ColorContentReduced,
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: 4,
                                '&:hover': {
                                  background:
                                    theme.semantic.ColorSurfaceHovered,
                                },
                              })}
                            >
                              <Icon size={16}>
                                <FilterIcon />
                              </Icon>
                            </button>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </OverlaySection>
      </OverlayPanel>

      {showHeatmaps && (
        <HeatmapLayer
          map={map}
          metropolisId={metropolisId}
          activeType={activeHeatmap}
          filters={heatmapFilters}
          hiddenCategories={hiddenHeatmapCategories}
          onLoadingChange={setHeatmapLoading}
          onFilterOptionsChange={setHeatmapFilterOptions}
          onCategoriesChange={cats => {
            setHeatmapCategories(cats)
            setHiddenHeatmapCategories(new Set())
            setHeatmapDataLoaded(true)
          }}
          insertBeforeLayer={insertBeforeLayer}
        />
      )}

      {activeFilterType && activeFilterItem && (
        <OddFilterModal
          type={activeFilterType}
          label={activeFilterItem.label}
          metropolisId={metropolisId}
          currentConfig={
            oddFilterConfigs[activeFilterType] ?? DEFAULT_FILTER_CONFIG
          }
          onSave={(config, elements) =>
            handleFilterSave(activeFilterType, config, elements)
          }
          onClose={() => setActiveFilterType(null)}
        />
      )}

      {showHeatmaps && heatmapFilterDialog.isOpen && activeHeatmap && (
        <HeatmapFilterModal
          heatmapType={activeHeatmap}
          currentFilters={heatmapFilters}
          filterOptions={heatmapFilterOptions}
          onSave={filters => {
            setHeatmapFilters(filters)
          }}
          onClose={heatmapFilterDialog.closeDialog}
        />
      )}
    </>
  )
}
